import React from 'react';
import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';

import { Spinner } from '@barboxx/admin-components';
import { extractErrorMessage } from '@barboxx/utils';

interface DataEntry {
    id: number | string;
    [prop: string]: any;
}

interface Props {
    title?: string;
    data: DataEntry[];
    ItemComponent: React.ElementType;
    noDataText?: string;
    onClick?: () => void;
    onItemClick?: (data: any) => void;
    error?: unknown;
    loading?: boolean;
}

const Content = ({
    data,
    error,
    loading,
    ItemComponent,
    noDataText = 'No Data',
    onItemClick,
}: Props) => {
    let innerContent: React.ReactNode | null = null;
    switch (true) {
        case loading:
            innerContent = <Spinner />;
            break;
        case !!error:
            innerContent = (
                <Typography color="error" align="center">
                    {extractErrorMessage(error)}
                </Typography>
            );
            break;
        case data?.length > 0:
            return (
                <>
                    {data.map(d => {
                        const { id } = d;
                        return (
                            <ItemComponent
                                {...d}
                                key={id}
                                onItemClick={onItemClick}
                            />
                        );
                    })}
                </>
            );
        default:
            innerContent = <Typography>{noDataText}</Typography>;
    }
    return (
        <Box
            display="flex"
            flex={1}
            justifyContent="center"
            alignItems="center"
            padding={2}
        >
            {innerContent}
        </Box>
    );
};

const DataList = (props: Props) => {
    const { title, onClick, ...rest } = props;

    return (
        <Card sx={{ minHeight: 120 }} onClick={onClick}>
            {title && (
                <CardHeader
                    sx={theme => ({
                        borderBottom: `solid 1px ${theme.palette.divider}`,
                    })}
                    title={title}
                />
            )}
            <CardContent
                sx={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    padding: 0,
                    '&:last-child': {
                        paddingBottom: 0,
                    },
                })}
            >
                <Content {...rest} />
            </CardContent>
        </Card>
    );
};

export default DataList;
